import api from './api';
import { User, ApiResponse } from './types';

/**
 * Users Service
 * Handles all user-related API requests
 */
const usersService = {
    /**
     * Get all users
     * @returns Promise<User[]>
     */
    getAll: async (): Promise<User[]> => {
        try {
            const response = await api.get<any>('/users');
            // Handle different response structures
            // If response is wrapped in data property
            if (response.data && Array.isArray(response.data)) {
                return response.data;
            }
            // If response is direct array
            if (Array.isArray(response)) {
                return response;
            }
            // If response is wrapped in users property
            if (response.users && Array.isArray(response.users)) {
                return response.users;
            }
            // Default to empty array if structure is unexpected
            console.warn('Unexpected API response structure:', response);
            return [];
        } catch (error) {
            console.error('Error fetching users:', error);
            throw error;
        }
    },

    /**
     * Get user by ID
     * @param id - User ID
     * @returns Promise<User>
     */
    getById: async (id: number): Promise<User> => {
        try {
            const response = await api.get<User>(`/users/${id}`);
            return response;
        } catch (error) {
            console.error(`Error fetching user ${id}:`, error);
            throw error;
        }
    },
    /**
     * Get user by ID
     * @param email - User ID
     * @returns Promise<User>
     */
    getByEmail: async (email: string): Promise<User> => {
        try {
            const response = await api.get<User>(`/users/email/${email}`);
            return response;
        } catch (error) {
            console.error(`Error fetching user ${email}:`, error);
            throw error;
        }
    },

    /**
     * Create new user
     * @param userData - User data
     * @returns Promise<User>
     */
    create: async (userData: Partial<User>): Promise<User> => {
        try {
            const response = await api.post<User>('/users', userData);
            return response;
        } catch (error) {
            console.error('Error creating user:', error);
            throw error;
        }
    },

    /**
     * Update user
     * @param id - User ID
     * @param userData - Updated user data
     * @returns Promise<User>
     */
    update: async (id: string, userData: Partial<User>): Promise<User> => {
        try {
            const response = await api.put<User>(`/users/${id}`, userData);
            return response;
        } catch (error) {
            console.error(`Error updating user ${id}:`, error);
            throw error;
        }
    },

    /**
     * Delete user
     * @param id - User ID
     * @returns Promise<void>
     */
    delete: async (id: string): Promise<void> => {
        try {
            await api.delete(`/users/${id}`);
        } catch (error) {
            console.error(`Error deleting user ${id}:`, error);
            throw error;
        }
    },

    /**
     * Get users with pagination
     * @param page - Page number
     * @param limit - Items per page
     * @returns Promise<User[]>
     */
    getPaginated: async (page: number = 1, limit: number = 10): Promise<User[]> => {
        try {
            const response = await api.get<User[]>('/users', {
                params: { page, limit }
            });
            return response;
        } catch (error) {
            console.error('Error fetching paginated users:', error);
            throw error;
        }
    },

    /**
     * Search users
     * @param query - Search query
     * @returns Promise<User[]>
     */
    search: async (query: string): Promise<User[]> => {
        try {
            const response = await api.get<User[]>('/users/search', {
                params: { q: query }
            });
            return response;
        } catch (error) {
            console.error('Error searching users:', error);
            throw error;
        }
    },
};

export default usersService;